// Folds one page of an auction's offline delta (BL-2339) into the device store (BL-2344): lots
// in, tombstoned lots out, changed note sets patched onto their lots. A sync's pages are applied
// in series order, and replaying a page is harmless, because every write is keyed.
import type { OfflineAuctionDeltaDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import { isQueuedLotCreate } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import type { OfflineStore, OfflineThumbnailAggregate } from '../../gateways/native-device';

/**
 * Lot ids a full re-download has written so far. At the end of the run, the auction's other
 * lots are removed (the delta's `mode: 'full'` replaces the device's set).
 */
export type SeenLotIds = Set<string>;

/**
 * Apply one delta page for an auction.
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction the page belongs to.
 * @param input.delta - the page.
 * @param input.seen - lot ids written during this run; this page's are added.
 * @returns once the page is stored.
 * @sideEffect Writes and deletes lot records in the device store.
 */
export async function applyAuctionDeltaPage(input: {
  store: OfflineStore;
  auctionId: string;
  delta: OfflineAuctionDeltaDto;
  seen: SeenLotIds;
}): Promise<void> {
  const { store, auctionId, delta, seen } = input;
  if (delta.lots.length > 0) {
    await store.records.putMany({
      collection: 'lots',
      items: delta.lots.map((lot) => ({ key: lot.detail.id, value: lot, parentKey: auctionId })),
    });
    for (const lot of delta.lots) seen.add(lot.detail.id);
  }
  if (delta.tombstones.length > 0) await dropTombstonedLots({ store, auctionId, tombstones: delta.tombstones });
  if (delta.noteUpdates.length > 0) {
    // One read and one batched write for the page's note sets, not a round trip per lot.
    const stored = await store.records.getMany({ collection: 'lots', keys: delta.noteUpdates.map((u) => u.lotId) });
    const items = delta.noteUpdates.flatMap((update, i) => {
      const lot = stored[i];
      return lot == null ? [] : [{ key: update.lotId, value: { ...lot, notes: update.notes }, parentKey: auctionId }];
    });
    if (items.length > 0) await store.records.putMany({ collection: 'lots', items });
  }
}

/**
 * @param delta - one delta page.
 * @returns whether the page changes anything on the device.
 */
export function pageChangesDevice(delta: OfflineAuctionDeltaDto): boolean {
  return delta.mode === 'full' || delta.lots.length > 0 || delta.tombstones.length > 0 || delta.noteUpdates.length > 0;
}

/**
 * Drop a page's tombstoned lots in one batch (BL-2429). A lot that left for another auction is
 * kept when that auction's sync already stored it there: the lot record is keyed by lot id, so the
 * move must not delete it.
 */
async function dropTombstonedLots(input: {
  store: OfflineStore;
  auctionId: string;
  tombstones: OfflineAuctionDeltaDto['tombstones'];
}): Promise<void> {
  const { store, auctionId, tombstones } = input;
  const departed = tombstones.filter((t) => t.reason === 'departed').map((t) => t.lotId);
  const stored = departed.length > 0 ? await store.records.getMany({ collection: 'lots', keys: departed }) : [];
  const movedAway = new Set(
    departed.filter((_, i) => {
      const lot = stored[i];
      return lot != null && lot.detail.auctionId !== auctionId;
    }),
  );
  const keys = tombstones.map((t) => t.lotId).filter((lotId) => !movedAway.has(lotId));
  await store.records.deleteMany({ collection: 'lots', keys });
}

/** The ids of lots created offline whose create is still in the queue: the server has never seen them. */
async function queuedLotCreateIds(store: OfflineStore): Promise<ReadonlySet<string>> {
  // BL-2681: a lot made offline is a create state; a relist's copy is still a queued entry.
  const states = (await store.lotStates.list()).filter((state) => state.kind === 'create').map((state) => state.lotId);
  return new Set([...(await store.outbox.list()).filter(isQueuedLotCreate).map((entry) => entry.id), ...states]);
}

/** Delete the given lots of an auction, except a lot whose create is still queued (BL-2431, Q3). */
async function deleteUnqueuedLots(store: OfflineStore, lotIds: string[]): Promise<number> {
  if (lotIds.length === 0) return 0;
  const queued = await queuedLotCreateIds(store);
  const deletable = lotIds.filter((lotId) => !queued.has(lotId));
  return deletable.length === 0 ? 0 : store.records.deleteMany({ collection: 'lots', keys: deletable });
}

/**
 * End a full re-download: remove the auction's lots that the run did not write, except a lot
 * created offline whose create is still queued (BL-2431, operator decision Q3): the server has
 * never seen it, so no download can carry it, and the replay drops it once its create lands.
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction.
 * @param input.seen - the lot ids the run wrote.
 * @returns how many lots were removed.
 * @sideEffect Deletes lot records from the device store.
 */
export async function pruneUnseenLots(input: { store: OfflineStore; auctionId: string; seen: SeenLotIds }): Promise<number> {
  const { store, auctionId, seen } = input;
  const entries = await store.records.entriesByParent({ collection: 'lots', parentKey: auctionId });
  return deleteUnqueuedLots(store, entries.map((entry) => entry.key).filter((lotId) => !seen.has(lotId)));
}

/**
 * Remove an auction's lots from the device (its switch turned off, or it left eligibility), except
 * a lot created offline whose create is still queued (BL-2431): that create still needs its record.
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction.
 * @returns how many lots were removed.
 * @sideEffect Deletes lot records from the device store.
 */
export async function dropAuctionLots(input: { store: OfflineStore; auctionId: string }): Promise<number> {
  const { store, auctionId } = input;
  const entries = await store.records.entriesByParent({ collection: 'lots', parentKey: auctionId });
  return deleteUnqueuedLots(store, entries.map((entry) => entry.key));
}

/** What an auction takes on the device, and its thumbnails' share of it (BL-2609). */
export interface AuctionSize {
  /** Its lot records (as UTF-8 JSON) plus its thumbnails. */
  bytes: number;
  thumbnails: OfflineThumbnailAggregate;
}

/**
 * @param entries - stored entries' sizes.
 * @returns how many, and their total.
 */
export function aggregateOf(entries: readonly { bytes: number }[]): OfflineThumbnailAggregate {
  return { count: entries.length, bytes: entries.reduce((sum, entry) => sum + entry.bytes, 0) };
}

/**
 * The bytes an auction takes on the device: its lot records (as UTF-8 JSON, read from the parent
 * index, so no lot is loaded, BL-2429) and its thumbnails. The thumbnails' aggregate is the one
 * given, when the caller holds a current one (the auction record's, BL-2609); otherwise it is
 * measured from the store, without loading an image.
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction.
 * @param input.thumbnails - a current thumbnail aggregate, to skip measuring them.
 * @returns the auction's size and its thumbnails' aggregate.
 */
export async function measureAuctionBytes(input: {
  store: OfflineStore;
  auctionId: string;
  thumbnails?: OfflineThumbnailAggregate;
}): Promise<AuctionSize> {
  const [lots, thumbnails] = await Promise.all([
    input.store.records.entriesByParent({ collection: 'lots', parentKey: input.auctionId }),
    input.thumbnails ?? input.store.thumbnails.entriesByAuction(input.auctionId).then(aggregateOf),
  ]);
  return { bytes: aggregateOf(lots).bytes + thumbnails.bytes, thumbnails };
}

/**
 * A cursor older than the delta's 30-day departure retention, which the server answers with a
 * full re-download, never a delta (ADR 066 — Offline Mode § 4.5).
 */
const FULL_DOWNLOAD_CURSOR = { sequence: 0, at: new Date(0).toISOString() };

/**
 * Make the auction's next sync a full re-download, so the device drops a change the server never
 * took (a discarded queue entry, BL-2345). The auction stays listed on the device meanwhile.
 *
 * @param input.store - the scope's device store.
 * @param input.auctionId - the auction to re-download.
 * @returns once the cursor is reset; nothing when the auction is not on the device.
 * @sideEffect Rewrites the auction record's sync cursor.
 */
export async function requestFullDownload(input: { store: OfflineStore; auctionId: string }): Promise<void> {
  const { store, auctionId } = input;
  const record = await store.records.get({ collection: 'auctions', key: auctionId });
  if (!record) return;
  await store.records.put({ collection: 'auctions', key: auctionId, value: { ...record, cursor: FULL_DOWNLOAD_CURSOR } });
}
